import React, { useRef, useState } from "react";

import SectionTitle from "@app/components/3_modules/sections/SectionTitle";
import moment from 'moment';

import { Building2, ChevronUp, ChevronDown } from "lucide-react";
import { useAuth } from '@app/contexts/AuthContext';
import { apiCall } from '@app/utils/apiCall';
import { useDossier, useDossierDispatch, DOSSIER_ACTIONS } from "@app/contexts/DossierContext";
import { useToast } from '@app/components/3_modules/ToastContainer';

import ButtonPrimary from "@app/components/1_elements/ButtonPrimary";
import Badge from "@app/components/1_elements/Badge";
import { initTaxReturnObject, buildTaxReturnFormDataBuilder } from "@app/utils/form_data_builders/TaxReturnFormDataBuilder";
import InputText from "@app/components/2_widgets/inputs/InputText";
import InputDate from "@app/components/2_widgets/inputs/InputDate";
import { calculateTaxReturnsTotal } from "@app/utils/tax_return";
import { useTranslation } from "react-i18next";
import { formatNumberDisplay } from "@app/utils/util";

export default function AddedValue({ }) {
    const dossier = useDossier();

    const [displaying, setDisplaying] = useState(false);
    const [taxReturn, setTaxReturn] = useState(dossier.tax_return ? dossier.tax_return : initTaxReturnObject(dossier.id));

    const auth = useAuth();
    const { t } = useTranslation();
    
    const dossierDispatch = useDossierDispatch();
    const toast = useToast();

    const handleChange = (key, value) => {
        setTaxReturn(prev => {
            // Split the keys
            const keys = key.split('.');            
            const newTaxReturn = { ...prev };

            let current = newTaxReturn;
            
            for (let i = 0; i < keys.length - 1; i++) {
                const currentKey = keys[i];
            
                // If does not exist, create the object
                if (!current[currentKey]) {
                    current[currentKey] = {};
                }
            
                current = current[currentKey];
            }

            // Update the keys
            current[keys[keys.length - 1]] = value;

            return newTaxReturn;
        });
    };
    
    function updateTaxReturn(event) {
        event.preventDefault();

        taxReturn.status = "validated";
        const formData = buildTaxReturnFormDataBuilder(taxReturn);

        if (taxReturn.id)
            apiCall({
                method: "patch",
                path: `/api/v1/tax_returns/${taxReturn.id}`,
                auth: auth,
                data: formData,
                onSuccess: (response) => {
                    const tax_return = response.data.tax_return;

                    setTaxReturn(tax_return);
                    dossierDispatch({
                        type: DOSSIER_ACTIONS.UPDATE_TAXRETURN,
                        payload: tax_return
                    });                    
                    toast.addToast("Données Fiscales validées !")
                }
            });
        else
            apiCall({
                method: "post",
                path: `/api/v1/tax_returns`,
                auth: auth,
                data: formData,
                onSuccess: (response) => {
                    const tax_return = response.data.tax_return;

                    setTaxReturn(tax_return);
                    dossierDispatch({
                        type: DOSSIER_ACTIONS.UPDATE_TAXRETURN,
                        payload: tax_return
                    });
                    toast.addToast("Données Fiscales validées !")
                }
            });            
    }

    function displayContent() {
        if (!displaying)
            return;        

        const taxReturnTotal = calculateTaxReturnsTotal(taxReturn.data);

        return (
            <div>
                <table className="custom-table">
                    <thead>
                        <tr>
                            <th key="1">
                                Réf.
                            </th>
                            <th key="2">
                                Champ demandé
                            </th>
                            <th key="3">
                                Valeur
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayClosingDate()}
                        {displayStartingDate()}
                        {displayRowText("A", "CA case A1/A2 de 1329-DEF", taxReturn.data?.a1a2_1329, "data.a1a2_1329")}
                        {displayRowText("A", "CA case A3 de 1330-CVAE-SD", taxReturn.data?.a3_1330, "data.a3_1330")}
                        {displayRowText("B", "Achats marchandises (FS - 2052-SD)", taxReturn.data?.fs_2052, "data.fs_2052")}
                        {displayRowText("C", "Achats matières premières (FU - 2052-SD)", taxReturn.data?.fu_2052, "data.fu_2052")}
                        {displayRowText("D", "Autres achats/charges externes (FW - 2052-SD)", taxReturn.data?.fw_2052, "data.fw_2052")}
                        {displayRowText("E", "Redevances brevets/licences (A4 - 2053-SD)", taxReturn.data?.a4_2053, "data.a4_2053")}
                        {displayRowText("F", "Intérêts et charges (GR - 2052-SD)", taxReturn.data?.gr_2052, "data.gr_2052")}
                        {displayRowText("G", "Charges cessions VMP (GT - 2052-SD)", taxReturn.data?.gt_2052, "data.gt_2052")}
                        {displayRowText("H", "Total (B + C + D + E + F + G)", formatNumberDisplay(taxReturnTotal.totalCharges), null, true)}
                        {displayRowText("I", "Cotisations syndicales (ES - 2058-C-SD)", taxReturn.data?.es_2058, "data.es_2058")}
                        {displayRowText("J", "Valeur ajoutée (A - H + I)", formatNumberDisplay(taxReturnTotal.addedValue), null, true)}
                    </tbody>
                </table> 
                <div className="text-end">
                    <ButtonPrimary label="Valider les données fiscales" onClick={updateTaxReturn}/>
                </div>
            </div>
        )        
    }

    /** 
     * Specific handle for closing date 
     * that must init starting_date also if empty this value is empty
     * based on the closing date
    */
    function handleClosingDate(v) {
        handleChange("fiscal_closing_date", v);
        // Update starting date depending on the closing date        
        const startingDate = moment(v).subtract(1, "year").add(1, "day");
        handleChange("fiscal_starting_date", startingDate);
    }

    function displayClosingDate() {
        return (
            <tr>
                <td></td>
                <td>Date de clôture d'exercice</td>
                <td>
                    <InputDate 
                        inputId="date"
                        label=""
                        defaultValue={taxReturn.fiscal_closing_date}
                        callbackOnChange={(v) => handleClosingDate(v)}
                    />
                </td>
            </tr>
        )
    }

    function displayStartingDate() {
        // Pre calculate the starting date if no starting date had be defined
        const autoDate = taxReturn.fiscal_closing_date ? moment(taxReturn.fiscal_closing_date).subtract(1, "year").add(1, "day") : null;
        
        return (
            <tr>
                <td></td>
                <td>Date de début d'exercice (auto: {autoDate?.format("DD-MM-YYYY")})</td>
                <td>
                    <InputDate 
                        inputId="date"
                        label=""
                        defaultValue={taxReturn.fiscal_starting_date || autoDate}
                        callbackOnChange={(v) => handleChange("fiscal_starting_date", v)}
                    />
                </td>
            </tr>
        )
    }

    function displayRowText(refC, label, value, key, readOnly=false) {
        return (
            <tr>
                <td>{refC}</td>
                <td>{label}</td>
                <td>
                    <InputText 
                        inputId={key}
                        placeholder="0"
                        defaultValue={value}
                        isReadOnly={readOnly}
                        callbackOnChange={(v) => handleChange(key, v)}
                    />
                </td>
            </tr>
        )
    }
    
    return (
        <div className="page-section">
            <SectionTitle
                icon={<Building2 />}
                title="Données Fiscales"
                onClick={() => setDisplaying(!displaying)}
                rightIcon={displaying ? <ChevronUp /> : <ChevronDown />}
                subtitle="Saisissez manuellement les valeurs issues de vos documents fiscaux"
                badge={<Badge label={t(`tax_return.status.${taxReturn.status}`)} classProp={`badge-${taxReturn.status}`} />}
            />
            {displayContent()}            
        </div>
    )
}